import type { ColumnDef } from '@tanstack/react-table';
import { createSerialColumn } from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { formatDateTimeWithSeconds } from '@/utils/date';
import { StatusBadge } from './StatusBadge';
import type { DailyDashboardItem, AttendanceStatus } from '../types/attendance.types';

interface GetDailyDashboardColumnsParams {
  searchText: string | undefined;
}

export function getDailyDashboardColumns({
  searchText,
}: GetDailyDashboardColumnsParams): ColumnDef<DailyDashboardItem, unknown>[] {
  return [
    createSerialColumn<DailyDashboardItem>(),
    {
      accessorKey: 'amName',
      header: 'AM Name',
      cell: ({ getValue }) => {
        const amName = getValue() as string | null;
        return amName ? (
          <HighlightText text={amName} highlightedText={searchText} />
        ) : (
          <EmptyValue />
        );
      },
    },
    {
      accessorKey: 'zmName',
      header: 'ZM Name / ID (Cell)',
      cell: ({ row }) => {
        const { zmName, zmUsername, zmMobile } = row.original;
        if (!zmName) return <EmptyValue />;

        const idAndCell = [zmUsername, zmMobile === null ? null : `(${zmMobile})`]
          .filter(Boolean)
          .join(' ');

        return (
          <div className="min-w-0">
            <HighlightText text={zmName} highlightedText={searchText} />
            {idAndCell === '' ? null : (
              <span className="block text-xs tabular-nums text-muted-foreground">
                <HighlightText text={idAndCell} highlightedText={searchText} />
              </span>
            )}
          </div>
        );
      },
    },
    { accessorKey: 'thana', header: 'Thana', cell: ({ getValue }) => getValue() ?? <EmptyValue /> },
    {
      accessorKey: 'district',
      header: 'Zilla',
      cell: ({ getValue }) => getValue() ?? <EmptyValue />,
    },
    {
      accessorKey: 'division',
      header: 'Division',
      cell: ({ getValue }) => getValue() ?? <EmptyValue />,
    },
    {
      accessorKey: 'loginStartTime',
      header: 'Login Time',
      cell: ({ getValue }) => formatDateTimeWithSeconds(getValue() as string | null),
    },
    {
      accessorKey: 'workTimeStart',
      header: 'Work Start Time',
      cell: ({ getValue }) => formatDateTimeWithSeconds(getValue() as string | null),
    },
    {
      accessorKey: 'workTimeEnd',
      header: 'Work End Time',
      cell: ({ getValue }) => formatDateTimeWithSeconds(getValue() as string | null),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => <StatusBadge status={getValue() as AttendanceStatus} />,
    },
  ];
}
